import type { GroupSelectGroupedItems, GroupSelectItemsGroup } from '@/types/components/select'
import { isMobileUserAgent } from '@/utils/browser/isMobileUserAgent'
import { cn } from '@/utils/cn'
import type { ChangeEvent, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../common/accordion'
import { Checkbox } from '../common/checkbox'
import { Input } from '../common/input'
import { Label } from '../common/label'
import { PopoverContent, PopoverList, PopoverListItem } from '../common/popover'
import { ScrollArea } from '../common/scrollArea'
import { ArrowIcon } from '../icons/arrow'
import { SearchIcon } from '../icons/search'

export interface GroupedFilterSelectContentProps {
  groupedItems: GroupSelectGroupedItems
  selectedItems: string[]
  setSelectedItems: (items: string[]) => void
  name: string
  renderItemPrefix?: (item: string) => ReactNode
  searchNormalizer?: (text: string) => string
  className?: string
  scrollClassName?: string
}

type GroupedFilterListContentProps = Omit<GroupedFilterSelectContentProps, 'className'>

export const GroupedFilterListContent = ({
  groupedItems,
  scrollClassName,
  searchNormalizer,
  ...props
}: GroupedFilterListContentProps) => {
  const [search, setSearch] = useState<string>('')
  const [openGroups, setOpenGroups] = useState<string[]>([])
  const [userOpenedGroups, setUserOpenedGroups] = useState<string[]>([])

  const filteredGroupedItems = useMemo(
    () =>
      Object.entries(groupedItems).reduce((acc, [groupName, group]) => {
        const filteredGroupItems = group.items.filter((item) => {
          const lowerItem = item.toLowerCase()
          const lowerSearch = search.toLowerCase()

          if (lowerItem.includes(lowerSearch)) return true

          if (searchNormalizer) {
            return searchNormalizer(lowerItem).includes(lowerSearch)
          }

          return false
        })
        if (filteredGroupItems.length > 0) {
          acc[groupName] = { ...group, items: filteredGroupItems }
        }
        return acc
      }, {} as GroupSelectGroupedItems),
    [groupedItems, search, searchNormalizer]
  )

  const defaultExpandedGroups = useMemo(
    () =>
      Object.entries(filteredGroupedItems)
        .filter(
          ([_, group]) =>
            !group.isSelectable ||
            (search.trim() !== '' &&
              group.items.some((item) => {
                const lowerItem = item.toLowerCase()
                const lowerSearch = search.toLowerCase()
                if (lowerItem.includes(lowerSearch)) return true
                if (searchNormalizer) return searchNormalizer(lowerItem).includes(lowerSearch)
                return false
              }))
        )
        .map(([groupName]) => groupName),
    [filteredGroupedItems, search, searchNormalizer]
  )

  const onSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value)
  }, [])

  const handleValueChange = (newValue: string[]) => {
    const validGroups = newValue.filter((groupName) => groupName in filteredGroupedItems)
    setUserOpenedGroups(validGroups)
    const newExpandedGroups = defaultExpandedGroups.filter((groupName) => !validGroups.includes(groupName))
    setOpenGroups([...validGroups, ...newExpandedGroups])
  }

  useEffect(() => {
    const validUserGroups = userOpenedGroups.filter((groupName) => groupName in filteredGroupedItems)
    const newExpandedGroups = defaultExpandedGroups.filter((groupName) => !validUserGroups.includes(groupName))
    setOpenGroups([...validUserGroups, ...newExpandedGroups])
  }, [defaultExpandedGroups, filteredGroupedItems, userOpenedGroups])

  return (
    <>
      <div className="px-3 py-2">
        <div className="relative inline-flex w-full items-center">
          <SearchIcon size={16} className="pointer-events-none absolute left-3 select-none text-body-foreground" />
          <Input
            placeholder={`Find ${props.name}`}
            onChange={onSearchChange}
            value={search}
            inputMode="text"
            className="w-full rounded-xl bg-page pl-9 ring-inset"
          />
        </div>
      </div>
      <ScrollArea
        type="auto"
        className={cn(
          'max-h-40 min-h-10 border-t [&_[data-role=scroll-area-viewport]]:max-h-[inherit]',
          scrollClassName
        )}
      >
        <Accordion type="multiple" value={openGroups} onValueChange={handleValueChange}>
          <PopoverList>
            {Object.entries(filteredGroupedItems).map(([groupName, itemsGroup]) => (
              <GroupSelect key={groupName} groupName={groupName} itemsGroup={itemsGroup} {...props} />
            ))}
          </PopoverList>
        </Accordion>
      </ScrollArea>
    </>
  )
}

export const GroupedFilterSelectContent = ({
  groupedItems,
  className,
  scrollClassName,
  searchNormalizer,
  ...props
}: GroupedFilterSelectContentProps) => {
  function handleAutoFocus(e: Event) {
    if (isMobileUserAgent()) {
      return e.preventDefault()
    }
  }

  return (
    <PopoverContent onOpenAutoFocus={handleAutoFocus} className={cn('w-auto bg-surface', className)}>
      <GroupedFilterListContent
        groupedItems={groupedItems}
        scrollClassName={scrollClassName}
        searchNormalizer={searchNormalizer}
        {...props}
      />
    </PopoverContent>
  )
}

interface GroupSelectProps extends Omit<GroupedFilterSelectContentProps, 'groupedItems' | 'dropdownPrefix'> {
  groupName: string
  itemsGroup: GroupSelectItemsGroup
}

const GroupSelect = ({
  groupName,
  itemsGroup,
  selectedItems,
  setSelectedItems,
  renderItemPrefix,
  name,
}: GroupSelectProps) => {
  const isGroupSelected = itemsGroup.items.every((item) => selectedItems.includes(item))

  const handleGroupClick = () => {
    setSelectedItems(
      isGroupSelected
        ? selectedItems.filter((item) => !itemsGroup.items.includes(item))
        : [...selectedItems, ...itemsGroup.items.filter((item) => !selectedItems.includes(item))]
    )
  }

  return (
    <AccordionItem value={groupName} className="flex w-full flex-col">
      <PopoverListItem className="h-10 border-b">
        <GroupItem
          name={groupName}
          isActive={isGroupSelected}
          isSelectable={itemsGroup.isSelectable}
          onClick={handleGroupClick}
        />
      </PopoverListItem>
      <AccordionContent>
        {itemsGroup.items.map((item) => (
          <SelectItem
            key={item}
            item={item}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            renderItemPrefix={renderItemPrefix}
            name={name}
            itemsGroup={itemsGroup}
          />
        ))}
      </AccordionContent>
    </AccordionItem>
  )
}

interface GroupItemProps {
  name: string
  isActive: boolean
  isSelectable: boolean
  onClick: () => void
}

const GroupItem = ({ name, isActive, isSelectable, onClick }: GroupItemProps) => {
  return (
    <Label
      className={cn(
        'flex h-full w-full max-w-full items-center justify-between truncate px-4',
        'hocus-within:bg-surface'
      )}
    >
      <div className="flex items-center gap-2">
        <span className="font-bold">{name}</span>
        <AccordionTrigger className="group rounded-full focus-visible:ring">
          <ArrowIcon
            direction="down"
            size={20}
            className={cn('transition-transform', 'group-data-[state=open]:-scale-y-100')}
          />
        </AccordionTrigger>
      </div>
      {isSelectable && <Checkbox onCheckedChange={onClick} checked={isActive} className="ml-auto" />}
    </Label>
  )
}

interface SelectItemProps extends Omit<GroupSelectProps, 'groupName'> {
  item: string
}

const SelectItem = ({ item, itemsGroup, selectedItems, setSelectedItems, renderItemPrefix }: SelectItemProps) => {
  const isItemSelected = selectedItems.includes(item)

  const handleGroupItemClick = useCallback(
    (item: string, isItemSelected: boolean, selectedItems: string[], setSelectedItems: (items: string[]) => void) => {
      const newSelectedItems = isItemSelected
        ? selectedItems.filter((itemInArray) => itemInArray !== item)
        : [...selectedItems, item]

      setSelectedItems(newSelectedItems)
    },
    [setSelectedItems, itemsGroup, item, isItemSelected, selectedItems]
  )

  const handleSingleItemClick = useCallback(
    (item: string, isItemSelected: boolean, selectedItems: string[], setSelectedItems: (items: string[]) => void) => {
      setSelectedItems(
        isItemSelected ? selectedItems.filter((itemInArray) => itemInArray !== item) : [...selectedItems, item]
      )
    },
    [setSelectedItems, itemsGroup, item, isItemSelected, selectedItems]
  )

  const handleItemClick = () => {
    if (itemsGroup.isSelectable) {
      handleGroupItemClick(item, isItemSelected, selectedItems, setSelectedItems)
    } else {
      handleSingleItemClick(item, isItemSelected, selectedItems, setSelectedItems)
    }
  }

  return (
    <PopoverListItem className="h-10 border-t first:border-none">
      <Label
        className={cn('flex h-full w-full max-w-full justify-between truncate px-4 pl-5', 'hocus-within:bg-surface')}
      >
        <div className="flex max-w-full items-center gap-2 truncate">
          {renderItemPrefix?.(item)}
          <span>{item}</span>
        </div>
        <Checkbox onCheckedChange={handleItemClick} checked={isItemSelected} />
      </Label>
    </PopoverListItem>
  )
}
